/**
 * AJAX Page Transitions
 *
 * Intercepts internal link clicks, immediately shows a skeleton loading state,
 * fetches the new page via AJAX, and swaps #MainContent without full page reload.
 * Header/footer persist across navigations for instant perceived speed.
 */
(function () {
  'use strict';

  var MAIN_ID = 'MainContent';
  var TRANSITION_CLASS = 'page-transitioning';
  var isTransitioning = false;
  var abortController = null;

  // Skeleton HTML — injected inline for instant display
  var skeletonHTML =
    '<div class="page-transition-skeleton w-full px-4 md:px-8 py-8 animate-pulse">' +
      '<div class="max-w-screen-xl mx-auto">' +
        // Breadcrumb skeleton
        '<div class="flex gap-2 mb-6"><div class="h-3 w-16 bg-skeleton rounded"></div><div class="h-3 w-4 bg-skeleton rounded"></div><div class="h-3 w-24 bg-skeleton rounded"></div></div>' +
        // Product page skeleton (most common navigation target)
        '<div class="flex flex-col md:flex-row gap-8">' +
          // Image skeleton
          '<div class="w-full md:w-1/2"><div class="bg-skeleton rounded-corners w-full" style="padding-bottom:100%"></div></div>' +
          // Info skeleton
          '<div class="w-full md:w-1/2 flex flex-col gap-4">' +
            '<div class="h-4 w-24 bg-skeleton rounded"></div>' +
            '<div class="h-8 w-3/4 bg-skeleton rounded"></div>' +
            '<div class="h-6 w-20 bg-skeleton rounded"></div>' +
            '<div class="h-4 w-full bg-skeleton rounded mt-4"></div>' +
            '<div class="h-4 w-5/6 bg-skeleton rounded"></div>' +
            '<div class="h-4 w-4/6 bg-skeleton rounded"></div>' +
            '<div class="h-12 w-full bg-skeleton rounded-corners mt-6"></div>' +
          '</div>' +
        '</div>' +
      '</div>' +
    '</div>';

  /**
   * Check if a link should be handled by AJAX transitions
   */
  function shouldIntercept(link, event) {
    if (!link || !link.href) return false;
    // Modifier keys = new tab
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return false;
    // External links
    if (link.origin !== window.location.origin) return false;
    // Target blank
    if (link.target === '_blank' || link.target === '_parent') return false;
    // Hash-only links
    if (link.pathname === window.location.pathname && link.hash) return false;
    // Non-HTML resources
    var ext = link.pathname.split('.').pop();
    if (['pdf', 'zip', 'jpg', 'png', 'gif', 'svg', 'webp', 'js', 'css'].indexOf(ext) !== -1) return false;
    // Links with download attribute
    if (link.hasAttribute('download')) return false;
    // Links that Alpine handles (prevent double-handling)
    if (link.hasAttribute('@click.prevent') || link.hasAttribute('x-on:click.prevent')) return false;
    // Form actions, javascript: links
    if (link.protocol !== 'http:' && link.protocol !== 'https:') return false;
    // Cart links (need full reload for cart state)
    if (link.pathname.indexOf('/cart') === 0) return false;
    // Checkout
    if (link.pathname.indexOf('/checkout') === 0) return false;
    // Account pages
    if (link.pathname.indexOf('/account') === 0) return false;
    // Search with form submission
    if (link.closest('form')) return false;

    return true;
  }

  /**
   * Clean up Alpine components in an element before removal
   */
  function cleanupAlpine(el) {
    if (window.Alpine && window.Alpine.destroyTree) {
      try { window.Alpine.destroyTree(el); } catch (e) { /* ignore */ }
    }
  }

  /**
   * Initialize Alpine components in new content
   */
  function initAlpine(el) {
    if (window.Alpine && window.Alpine.initTree) {
      try { window.Alpine.initTree(el); } catch (e) { /* ignore */ }
    }
  }

  /**
   * Alpine component scripts that need re-registration on AJAX navigation.
   * These scripts use guard patterns (checkFileNotLoaded / loadedScript.includes)
   * and addEventListener('alpine:init') which only fires once.
   */
  var ALPINE_COMPONENT_SCRIPTS = [
    'media-gallery.js',
    'sticky-atc.js',
    'product-tabs.js',
    'collections.js',
    'product-components.js',
    'shipping-policy.js',
    'segmented-banner.js',
    'facets.js'
  ];

  /**
   * Check if a script src matches an Alpine component script
   */
  function isAlpineComponentScript(src) {
    if (!src) return false;
    return ALPINE_COMPONENT_SCRIPTS.some(function (name) {
      return src.indexOf(name) !== -1;
    });
  }

  /**
   * Reset guards and re-register Alpine components from already-loaded scripts.
   * These scripts use checkFileNotLoaded() or loadedScript.includes() guards
   * and listen for 'alpine:init' which only fires once on initial load.
   * Returns a Promise that resolves when all scripts have loaded.
   */
  function reRegisterAlpineComponents(container) {
    var scripts = container.querySelectorAll('script[src]');
    var scriptsToReload = [];

    scripts.forEach(function (script) {
      if (isAlpineComponentScript(script.src)) {
        scriptsToReload.push(script.src);
      }
    });

    if (scriptsToReload.length === 0) return Promise.resolve();

    // Reset the loadedScript guard so scripts can re-execute
    if (window.Maximize && window.Maximize.loadedScript) {
      ALPINE_COMPONENT_SCRIPTS.forEach(function (name) {
        var idx = window.Maximize.loadedScript.indexOf(name);
        if (idx !== -1) {
          window.Maximize.loadedScript.splice(idx, 1);
        }
      });
    }

    // Re-fetch and execute each Alpine component script, return Promise
    var loadPromises = scriptsToReload.map(function (src) {
      return new Promise(function (resolve) {
        // Remove the old script tag to allow re-execution
        var existing = document.querySelector('script[src="' + src + '"]');
        if (existing) existing.remove();

        var newScript = document.createElement('script');
        newScript.src = src;
        newScript.onload = function () {
          resolve();
        };
        newScript.onerror = function () {
          resolve(); // Don't block on errors
        };
        document.head.appendChild(newScript);
      });
    });

    return Promise.all(loadPromises).then(function () {
      // Scripts listen for 'alpine:init' which already fired on initial load.
      // Dispatch it again so the addEventListener callbacks trigger.
      document.dispatchEvent(new CustomEvent('alpine:init'));
      // Give requestAnimationFrame callbacks time to execute
      return new Promise(function (resolve) {
        requestAnimationFrame(function () {
          requestAnimationFrame(resolve);
        });
      });
    });
  }

  /**
   * Execute inline <script> tags in new content
   */
  function executeScripts(container) {
    var scripts = container.querySelectorAll('script');
    scripts.forEach(function (oldScript) {
      // Skip external scripts that are already loaded (except Alpine components)
      if (oldScript.src && document.querySelector('script[src="' + oldScript.src + '"]')) {
        // Alpine component scripts are handled by reRegisterAlpineComponents()
        return;
      }

      var newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach(function (attr) {
        newScript.setAttribute(attr.name, attr.value);
      });
      if (!oldScript.src) {
        newScript.textContent = oldScript.textContent;
      }
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }

  /**
   * Update page metadata (title, meta tags, body classes)
   */
  function updateMeta(doc) {
    // Title
    document.title = doc.title;

    // Update body class for template-specific styling
    var newBody = doc.querySelector('body');
    if (newBody) {
      // Preserve transition class, update template classes
      var currentClasses = document.body.className;
      document.body.className = newBody.className;
    }

    // Update canonical URL
    var newCanonical = doc.querySelector('link[rel="canonical"]');
    var oldCanonical = document.querySelector('link[rel="canonical"]');
    if (newCanonical && oldCanonical) {
      oldCanonical.href = newCanonical.href;
    }

    // Update meta description
    var newDesc = doc.querySelector('meta[name="description"]');
    var oldDesc = document.querySelector('meta[name="description"]');
    if (newDesc && oldDesc) {
      oldDesc.content = newDesc.content;
    }
  }

  /**
   * Update conditional CSS (product-page.css only on product pages)
   */
  function updateConditionalAssets(doc) {
    var productCssSelector = 'link[href*="product-page.css"]';
    var hasProductCss = !!doc.querySelector(productCssSelector);
    var currentProductCss = document.querySelector(productCssSelector);

    if (hasProductCss && !currentProductCss) {
      // Add product CSS
      var newLink = doc.querySelector(productCssSelector).cloneNode(true);
      document.head.appendChild(newLink);
    } else if (!hasProductCss && currentProductCss) {
      // Remove product CSS (optional — keeping it doesn't hurt)
    }
  }

  /**
   * Perform the page transition
   */
  function navigateTo(url, pushState) {
    if (isTransitioning) {
      // Abort previous transition
      if (abortController) abortController.abort();
    }

    isTransitioning = true;
    abortController = new AbortController();
    document.body.classList.add(TRANSITION_CLASS);

    var mainEl = document.getElementById(MAIN_ID);
    if (!mainEl) {
      // Fallback to normal navigation
      window.location.href = url;
      return;
    }

    // 1. Immediately show skeleton
    cleanupAlpine(mainEl);
    mainEl.innerHTML = skeletonHTML;
    window.scrollTo({ top: 0, behavior: 'instant' });

    // 2. Update URL immediately
    if (pushState !== false) {
      history.pushState({ pageTransition: true }, '', url);
    }

    // 3. Show progress bar
    var progressBar = document.getElementById('navProgress');
    if (progressBar) {
      progressBar.className = 'nav-progress';
      void progressBar.offsetWidth;
      progressBar.className = 'nav-progress active';
    }

    // 4. Fetch the new page
    fetch(url, { signal: abortController.signal })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');

        var newMain = doc.getElementById(MAIN_ID);
        if (!newMain) {
          // Fallback — no MainContent found, do full reload
          window.location.href = url;
          return;
        }

        // 5. Prevent Alpine MutationObserver from auto-initializing new content
        //    (avoids double x-teleport processing that causes duplicate filter bars)
        mainEl.setAttribute('x-ignore', '');

        // 6. Swap content
        mainEl.innerHTML = newMain.innerHTML;
        // Copy attributes (class changes for template type)
        Array.from(newMain.attributes).forEach(function (attr) {
          mainEl.setAttribute(attr.name, attr.value);
        });

        // 7. Update page metadata
        updateMeta(doc);
        updateConditionalAssets(doc);

        // 8. Execute inline scripts in new content
        executeScripts(mainEl);

        // 9. Re-register Alpine components, then initialize Alpine on new content
        reRegisterAlpineComponents(mainEl).then(function () {
          // Remove x-ignore so Alpine can process the new content
          mainEl.removeAttribute('x-ignore');
          initAlpine(mainEl);

          // 10. Dispatch events for analytics and Shopify apps
          window.dispatchEvent(new CustomEvent('page:loaded', { detail: { url: url } }));
          window.dispatchEvent(new Event('resize'));

          // Shopify analytics
          if (window.Shopify && window.Shopify.designMode === false) {
            try {
              if (window.ShopifyAnalytics && window.ShopifyAnalytics.lib) {
                window.ShopifyAnalytics.lib.page(url);
              }
            } catch (e) { /* ignore */ }
          }

          // Trigger animation reveals
          document.dispatchEvent(new CustomEvent('maximize:animation:reveal'));

          // Hide progress bar
          if (progressBar) {
            progressBar.className = 'nav-progress done';
          }
        });
      })
      .catch(function (err) {
        if (err.name === 'AbortError') return; // Intentional abort
        // On error, fall back to normal navigation
        console.warn('[page-transitions] Fetch failed, falling back:', err);
        window.location.href = url;
      })
      .finally(function () {
        isTransitioning = false;
        document.body.classList.remove(TRANSITION_CLASS);
      });
  }

  /**
   * Initialize event listeners
   */
  function init() {
    // Track current pathname to distinguish page navigations from query-param changes
    var currentPathname = window.location.pathname;

    // Intercept link clicks
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href]');
      if (!shouldIntercept(link, e)) return;

      e.preventDefault();
      // Close any open drawers/menus (mobile menu, cart drawer, etc.)
      window.dispatchEvent(new CustomEvent('menu-select'));
      if (window.Alpine && window.Alpine.store) {
        try {
          var mobileNav = window.Alpine.store('xMobileNav');
          if (mobileNav && mobileNav.close) mobileNav.close();
        } catch (err) { /* ignore */ }
        try {
          var drawer = window.Alpine.store('xMaximizeDrawer');
          if (drawer && drawer.handleClose) drawer.handleClose();
        } catch (err) { /* ignore */ }
      }
      currentPathname = new URL(link.href, window.location.origin).pathname;
      navigateTo(link.href, true);
    });

    // Handle browser back/forward
    window.addEventListener('popstate', function (e) {
      var newPathname = window.location.pathname;
      if (newPathname === currentPathname) {
        // Same page, only query params changed (e.g. facets filter/sort) — let page-specific JS handle it
        return;
      }
      currentPathname = newPathname;
      navigateTo(window.location.href, false);
    });

    // Mark initial page in history
    history.replaceState({ pageTransition: true }, '', window.location.href);
  }

  // Start after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
